const resolveProductVariant = (variants, selectedOptions) => {
  if (!selectedOptions.length) return variants.length === 1 ? variants[0] : null;

  return (
    variants.find(
      (variant) =>
        variant.options.length === selectedOptions.length &&
        variant.options.every((option, index) => option === selectedOptions[index])
    ) || null
  );
};

const getProductOptionValueState = (variants, selectedOptions, optionIndex, optionValue) => {
  const matchingVariants = variants.filter(
    (variant) =>
      variant.options.length === selectedOptions.length &&
      variant.options[optionIndex] === optionValue &&
      variant.options.every(
        (option, index) => index >= optionIndex || option === selectedOptions[index]
      )
  );

  if (!matchingVariants.length) return 'unavailable';
  return matchingVariants.some((variant) => variant.available) ? 'available' : 'sold-out';
};

class AureliaTheme {
  constructor() {
    this.body = document.body;
    this.activeDrawer = null;
    this.drawerTrigger = null;
    this.initialize();
  }

  initialize() {
    this.bindDrawers();
    this.bindProductForms();
    this.bindLocalization();
    this.bindDisclosure();
    this.bindCategoryTabs();
    this.bindRecommendations();
  }

  drawerFocusTargets(drawer) {
    return Array.from(drawer.querySelectorAll(
      'a[href], summary, button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
    )).filter((element) => element.tabIndex >= 0 && !element.closest('[hidden], [inert]') && element.getClientRects().length);
  }

  bindDrawers() {
    if (this.drawersBound) return;
    this.drawersBound = true;

    document.addEventListener('click', (event) => {
      const opener = event.target.closest?.('[data-drawer-open]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener.dataset.drawerOpen, opener);
        return;
      }
      if (event.target.closest?.('[data-drawer-close], [data-site-overlay]')) {
        this.closeDrawers();
      }
    });

    document.addEventListener('keydown', (event) => {
      const drawer = this.activeDrawer;
      if (!drawer?.isConnected || drawer.hidden || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawers();
        return;
      }
      if (event.key !== 'Tab') return;
      const targets = this.drawerFocusTargets(drawer);
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (!first) {
        event.preventDefault();
        drawer.focus();
      } else if (event.shiftKey && (document.activeElement === first || !targets.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !targets.includes(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('focusin', (event) => {
      const drawer = this.activeDrawer;
      if (!drawer?.isConnected || drawer.hidden || drawer.contains(event.target)) return;
      (this.drawerFocusTargets(drawer)[0] || drawer).focus();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (this.activeDrawer && event.target.contains(this.activeDrawer)) this.closeDrawers();
    });
  }

  openDrawer(name, trigger = document.activeElement) {
    const drawer = Array.from(document.querySelectorAll('[data-drawer]')).find(
      (candidate) => candidate.dataset.drawer === name
    );
    if (!drawer) return;
    const returnTrigger = trigger?.closest('[data-drawer]') ? this.drawerTrigger : trigger;
    this.closeDrawers({ restoreFocus: false, immediate: true });
    this.activeDrawer = drawer;
    this.drawerTrigger = returnTrigger;
    drawer.hidden = false;
    drawer.inert = false;
    drawer.removeAttribute('aria-hidden');
    if (!drawer.hasAttribute('tabindex')) drawer.tabIndex = -1;
    drawer.getBoundingClientRect();
    requestAnimationFrame(() => {
      if (this.activeDrawer !== drawer || !drawer.isConnected) return;
      drawer.classList.add('is-open');
      const targets = this.drawerFocusTargets(drawer);
      const focusTarget = targets.find((element) => element.matches('input[type="search"]'));
      (focusTarget || targets[0] || drawer).focus();
    });
    this.body.classList.add('drawer-open');
    document.querySelectorAll('[data-drawer-open]').forEach((opener) => {
      opener.setAttribute('aria-expanded', String(opener.dataset.drawerOpen === name));
    });

    const overlay = document.querySelector('[data-site-overlay]');
    if (overlay) {
      overlay.hidden = false;
      overlay.classList.add('is-visible');
    }
  }

  closeDrawers({ restoreFocus = true, immediate = false } = {}) {
    const trigger = this.drawerTrigger;
    const duration = immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
    this.drawerHideTimers ||= new Map();
    window.clearTimeout(this.overlayHideTimer);
    this.activeDrawer = null;
    this.drawerTrigger = null;
    document.querySelectorAll('[data-drawer]').forEach((drawer) => {
      window.clearTimeout(this.drawerHideTimers.get(drawer));
      this.drawerHideTimers.delete(drawer);
      drawer.classList.remove('is-open');
      drawer.inert = true;
      drawer.setAttribute('aria-hidden', 'true');
      if (duration && !drawer.hidden) {
        const timer = window.setTimeout(() => {
          drawer.hidden = true;
          this.drawerHideTimers.delete(drawer);
        }, duration);
        this.drawerHideTimers.set(drawer, timer);
      } else drawer.hidden = true;
      drawer.dispatchEvent(new CustomEvent('yevori:drawer-close'));
    });
    document.querySelectorAll('[data-drawer-open]').forEach((opener) => {
      opener.setAttribute('aria-expanded', 'false');
    });
    this.body.classList.remove('drawer-open');
    const overlay = document.querySelector('[data-site-overlay]');
    if (overlay) {
      overlay.classList.remove('is-visible');
      if (duration && !overlay.hidden) this.overlayHideTimer = window.setTimeout(() => { overlay.hidden = true; }, duration);
      else overlay.hidden = true;
    }
    if (restoreFocus && trigger?.isConnected) trigger.focus();
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      if (root.dataset.productFormBound === 'true') return;
      root.dataset.productFormBound = 'true';

      if (root.dataset.highVariantProduct === '10293046214879') {
        this.bindHighVariantProduct(root);
        return;
      }

      const variantsNode = root.querySelector('[data-product-variants]');
      if (!variantsNode) return;
      const productForm = root.querySelector('.product-form');
      const selectors = Array.from(root.querySelectorAll('[data-option-selector]'));
      const idInput = productForm?.querySelector('[name="id"]');
      const addButton = root.querySelector('[data-add-to-cart]');
      const addLabel = root.querySelector('[data-add-label]');
      const priceRow = root.querySelector('[data-product-price-row]');
      const price = root.querySelector('[data-product-price]');
      const comparePrice = root.querySelector('[data-compare-price]');
      const status = root.querySelector('[data-product-status]');
      const records = root.querySelector('[data-variant-records]');
      const stickyMeta = root.querySelector('[data-sticky-product-meta]');
      const stickyPrice = root.querySelector('[data-sticky-product-price]');
      const stickyComparePrice = root.querySelector('[data-sticky-compare-price]');
      const stickyOptions = root.querySelector('[data-sticky-product-options]');
      const stickyButton = root.querySelector('[data-sticky-add]');
      const mediaItems = Array.from(root.querySelectorAll('[data-media-id]'));
      const mediaCurrent = root.querySelector('[data-media-current]');
      const unavailableText =
        addButton?.dataset.unavailable || status?.dataset.unavailable || '';
      const soldOutText = addButton?.dataset.soldOut || status?.dataset.outOfStock || '';
      const availableText = addButton?.dataset.available || status?.dataset.inStock || '';
      let lastSelectionSignature = null;

      const setButtonState = (button, label, state) => {
        if (!button) return;
        const available = state === 'available';
        const pending = productForm?.dataset.addState === 'pending';
        const added = productForm?.dataset.addState === 'added';
        const disabled = !available || pending || added;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
        if (pending) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');

        const text = pending
          ? button.dataset.adding || 'Adding…'
          : added
            ? button.dataset.added || 'Added'
            : available
          ? button.dataset.available || availableText
          : state === 'sold-out'
            ? button.dataset.soldOut || soldOutText
            : button.dataset.unavailable || unavailableText;

        if (label) label.textContent = text;
        else button.textContent = text;
      };

      const renderComparePrice = (element, value) => {
        if (!element) return false;
        element.innerHTML = value || '';
        element.hidden = !value;
        return Boolean(value);
      };

      const renderOptionStates = (selectedOptions) => {
        root.querySelectorAll('[data-product-option-group]').forEach((group) => {
          const selector = group.querySelector('[data-option-selector]');
          const optionIndex = selectors.indexOf(selector);
          if (!selector || optionIndex < 0) return;

          const soldOutLabel = group.dataset.soldOutLabel || soldOutText;
          const unavailableLabel = group.dataset.unavailableLabel || unavailableText;

          group.querySelectorAll('[data-option-pill]').forEach((pill) => {
            const optionState = getProductOptionValueState(
              variants,
              selectedOptions,
              optionIndex,
              pill.dataset.optionValue
            );
            const selected = pill.dataset.optionValue === selector.value;
            const unavailable = optionState === 'unavailable';
            const availability = pill.querySelector('[data-option-availability]');

            pill.classList.toggle('is-selected', selected);
            pill.classList.toggle('is-sold-out', optionState === 'sold-out');
            pill.classList.toggle('is-unavailable', unavailable);
            pill.setAttribute('aria-pressed', String(selected));
            pill.setAttribute('aria-disabled', String(unavailable));
            pill.disabled = unavailable;
            pill.dataset.optionState = optionState;

            if (availability) {
              availability.textContent =
                optionState === 'sold-out'
                  ? soldOutLabel
                  : unavailable
                    ? unavailableLabel
                    : '';
            }
          });
        });
      };

      const renderMedia = (variant) => {
        const mediaId = variant?.featured_media?.id;
        if (!mediaId) return;

        const target = mediaItems.find((media) => media.dataset.mediaId === String(mediaId));
        if (!target || target.dataset.mediaActive === 'true') return;

        mediaItems.forEach((media) => {
          media.dataset.mediaActive = String(media === target);
        });
        root.querySelector('[data-product-media-gallery]')?.dispatchEvent(
          new CustomEvent('product:media-change', { detail: { mediaId } })
        );
        if (mediaCurrent && target.dataset.mediaPosition) {
          mediaCurrent.textContent = target.dataset.mediaPosition;
        }

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      };

      const syncUrl = (variant) => {
        const url = new URL(window.location.href);
        const variantId = variant?.id ? String(variant.id) : '';
        if (variantId) {
          if (url.searchParams.get('variant') === variantId) return;
          url.searchParams.set('variant', variantId);
        } else {
          if (!url.searchParams.has('variant')) return;
          url.searchParams.delete('variant');
        }
        window.history.replaceState({}, '', url);
      };

      const renderUnavailableState = (selectedOptions, { updateUrl = false } = {}) => {
        if (idInput) idInput.value = '';
        if (productForm) {
          productForm.dataset.variantState = 'unavailable';
          delete productForm.dataset.currentVariantId;
        }
        if (price) price.textContent = '';
        if (stickyPrice) stickyPrice.textContent = '';
        renderComparePrice(comparePrice, '');
        renderComparePrice(stickyComparePrice, '');
        priceRow?.classList.remove('is-on-sale');
        stickyMeta?.classList.remove('is-on-sale');
        if (stickyOptions) stickyOptions.textContent = selectedOptions.join(' / ');
        setButtonState(addButton, addLabel, 'unavailable');
        setButtonState(stickyButton, null, 'unavailable');
        if (status) {
          status.textContent = unavailableText;
          status.classList.add('is-unavailable');
        }
        if (updateUrl) syncUrl(null);
      };

      let variants = [];
      try {
        variants = JSON.parse(variantsNode.textContent);
      } catch (error) {
        renderUnavailableState(selectors.map((selector) => selector.value));
        return;
      }

      if (!Array.isArray(variants) || !variants.length) {
        renderUnavailableState(selectors.map((selector) => selector.value));
        return;
      }

      const renderProductState = ({ updateUrl = true, updateMedia = true } = {}) => {
        const selectedOptions = selectors.map((selector) => selector.value);
        const variant = resolveProductVariant(variants, selectedOptions);
        const signature = `${selectedOptions.join('\u0000')}|${variant?.id || ''}`;

        if (signature === lastSelectionSignature) return;
        lastSelectionSignature = signature;
        renderOptionStates(selectedOptions);

        if (!variant) {
          renderUnavailableState(selectedOptions, { updateUrl });
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (productForm) {
          productForm.dataset.variantState = variant.available ? 'available' : 'sold-out';
          productForm.dataset.currentVariantId = variant.id;
        }
        const record = records
          ? records.querySelector(`[data-variant-id="${variant.id}"]`)
          : null;

        if (record && price) price.innerHTML = record.dataset.price;
        if (record && stickyPrice) stickyPrice.innerHTML = record.dataset.price;
        const compareValue = record?.dataset.comparePrice || '';
        const onSale = renderComparePrice(comparePrice, compareValue);
        renderComparePrice(stickyComparePrice, compareValue);
        priceRow?.classList.toggle('is-on-sale', onSale);
        stickyMeta?.classList.toggle('is-on-sale', onSale);
        if (stickyOptions) stickyOptions.textContent = variant.options.join(' / ');

        const purchaseState = variant.available ? 'available' : 'sold-out';
        setButtonState(addButton, addLabel, purchaseState);
        setButtonState(stickyButton, null, purchaseState);

        if (status) {
          status.textContent = variant.available
            ? status.dataset.inStock
            : status.dataset.outOfStock;
          status.classList.toggle('is-unavailable', !variant.available);
        }

        if (updateUrl) syncUrl(variant);
        if (updateMedia) renderMedia(variant);
      };

      selectors.forEach((selector) =>
        selector.addEventListener('change', () => renderProductState())
      );
      if (productForm) {
        productForm.addEventListener('submit', (event) => {
          if (productForm.dataset.variantState !== 'available' || !idInput?.value || productForm.dataset.addState === 'pending' || productForm.dataset.addState === 'added') {
            event.preventDefault();
          }
        });
      }

      const initialVariantParam = new URL(window.location.href).searchParams.get('variant');
      const hasValidInitialVariant = variants.some(
        (variant) => String(variant.id) === initialVariantParam
      );
      renderProductState({
        updateUrl: Boolean(initialVariantParam && !hasValidInitialVariant),
        updateMedia: false,
      });
    });
  }

  bindHighVariantProduct(root) {
    const form = root.querySelector('.product-form');
    const idInput = form?.querySelector('[name="id"]');
    const selectors = Array.from(root.querySelectorAll('[data-option-selector]'));
    const addButton = root.querySelector('[data-add-to-cart]');
    const stickyButton = root.querySelector('[data-sticky-add]');
    const status = root.querySelector('[data-product-status]');
    const payment = root.querySelector('[data-high-variant-payment]');
    const stateNode = root.querySelector('[data-high-variant-state]');
    if (!form || !idInput || !selectors.length || !stateNode) return;

    const selection = () => selectors.map((selector) => selector.selectedOptions[0]?.dataset.optionValueId || '');
    const names = () => selectors.map((selector) => selector.value);
    const same = (left, right) => left.length === right.length && left.every((value, index) => value === right[index]);
    const validId = (value) => typeof value === 'string' && /^\d+$/.test(value);
    const unavailableText = addButton?.dataset.unavailable || status?.dataset.unavailable || '';
    let requestVersion = 0;
    let controller = null;
    let committedOptionIds = [];
    let deferredHistoryUrl = null;

    const addLocked = () => ['pending', 'added'].includes(form.dataset.addState);
    const canPurchase = () => form.dataset.variantState === 'available' && Boolean(idInput.value) &&
      idInput.value === form.dataset.currentVariantId && !addLocked() && !deferredHistoryUrl;

    const syncPurchaseControls = () => {
      if (!addLocked() && deferredHistoryUrl) {
        const url = deferredHistoryUrl;
        deferredHistoryUrl = null;
        requestState(url);
        return;
      }
      const pending = form.dataset.addState === 'pending';
      const added = form.dataset.addState === 'added';
      const loading = form.dataset.variantState === 'loading';
      [addButton, stickyButton].forEach((button) => {
        if (!button) return;
        button.disabled = !canPurchase();
        button.setAttribute('aria-disabled', String(button.disabled));
        if (pending || loading) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
        const label = button.querySelector('[data-add-label]') || button;
        label.textContent = pending ? button.dataset.adding : added ? button.dataset.added :
          loading ? root.dataset.selectionLoading : form.dataset.variantState === 'available' ? button.dataset.available :
            form.dataset.variantState === 'sold-out' ? button.dataset.soldOut : button.dataset.unavailable;
      });
      if (payment) {
        payment.hidden = !canPurchase();
        payment.inert = !canPurchase();
      }
      selectors.forEach((selector) => { selector.disabled = addLocked(); });
      root.querySelectorAll('[data-option-pill]').forEach((pill) => {
        pill.disabled = addLocked();
        pill.setAttribute('aria-disabled', String(pill.disabled));
      });
    };

    const renderPills = () => {
      selectors.forEach((selector) => {
        const group = selector.closest('[data-product-option-group]');
        group?.querySelectorAll('[data-option-pill]').forEach((pill) => {
          const option = Array.from(selector.options).find((item) => item.value === pill.dataset.optionValue);
          const selected = selector.value === pill.dataset.optionValue;
          const available = option?.dataset.optionAvailable === 'true';
          pill.classList.toggle('is-selected', selected);
          pill.classList.toggle('is-unavailable', !available);
          pill.classList.remove('is-sold-out');
          pill.setAttribute('aria-pressed', String(selected));
          pill.setAttribute('aria-disabled', String(addLocked()));
          pill.disabled = addLocked();
          pill.dataset.optionState = available ? 'available' : 'unavailable';
          const label = pill.querySelector('[data-option-availability]');
          if (label) label.textContent = available ? '' : group.dataset.unavailableLabel || unavailableText;
        });
      });
    };

    const renderPrice = (variant) => {
      root.querySelectorAll('[data-product-price], [data-sticky-product-price]').forEach((node) => {
        node.innerHTML = variant?.price || '';
      });
      root.querySelectorAll('[data-compare-price], [data-sticky-compare-price]').forEach((node) => {
        node.innerHTML = variant?.comparePrice || '';
        node.hidden = !variant?.comparePrice;
      });
      root.querySelectorAll('[data-product-price-row], [data-sticky-product-meta]').forEach((node) => {
        node.classList.toggle('is-on-sale', Boolean(variant?.comparePrice));
      });
      const stickyOptions = root.querySelector('[data-sticky-product-options]');
      if (stickyOptions) stickyOptions.textContent = names().filter(Boolean).join(' / ');
    };

    const invalidate = (state, message) => {
      idInput.value = '';
      idInput.disabled = true;
      form.dataset.variantState = state;
      delete form.dataset.currentVariantId;
      stateNode.textContent = JSON.stringify({ productId: root.dataset.highVariantProduct, sectionId: root.dataset.sectionId, variant: null });
      renderPrice(null);
      if (status) {
        status.textContent = message;
        status.classList.add('is-unavailable');
      }
      syncPurchaseControls();
      renderPills();
    };

    const readState = (source) => {
      if (!source || source.dataset.highVariantProduct !== root.dataset.highVariantProduct ||
        source.dataset.sectionId !== root.dataset.sectionId) throw new Error('Product section mismatch');
      const payload = JSON.parse(source.querySelector('[data-high-variant-state]')?.textContent || 'null');
      if (!payload || payload.productId !== root.dataset.highVariantProduct || payload.sectionId !== root.dataset.sectionId) {
        throw new Error('Product state mismatch');
      }
      const sourceSelectors = Array.from(source.querySelectorAll('[data-option-selector]'));
      if (sourceSelectors.length !== selectors.length) throw new Error('Option count mismatch');
      const groups = sourceSelectors.map((selector, index) => {
        const values = Array.from(selector.options).map((option) => ({
          id: option.dataset.optionValueId, name: option.value, available: option.dataset.optionAvailable,
        }));
        const expected = Array.from(selectors[index].options);
        if (values.length !== expected.length || values.some((value, valueIndex) => !validId(value.id) ||
          value.id !== expected[valueIndex].dataset.optionValueId || value.name !== expected[valueIndex].value ||
          !['true', 'false'].includes(value.available))) throw new Error('Option values mismatch');
        const selectedId = selector.selectedOptions[0]?.dataset.optionValueId;
        if (!values.some((value) => value.id === selectedId)) throw new Error('Selected option missing');
        return { values, selectedId, selectedName: selector.value };
      });
      const variant = payload.variant;
      if (variant !== null && (!variant || !validId(variant.id) || typeof variant.available !== 'boolean' ||
        !Array.isArray(variant.options) || !same(variant.options, groups.map((group) => group.selectedName)) ||
        typeof variant.price !== 'string' || !variant.price || typeof variant.comparePrice !== 'string' ||
        !(variant.mediaId === null || validId(variant.mediaId)))) throw new Error('Invalid variant state');
      return { payload, groups };
    };

    const matchesUrl = (state, url) => {
      const variantId = url.searchParams.get('variant');
      if (url.searchParams.has('variant') && (!validId(variantId) || state.payload.variant?.id !== variantId)) return false;
      if (url.searchParams.has('option_values')) {
        const ids = url.searchParams.get('option_values').split(',');
        const selectedIds = state.groups.map((group) => group.selectedId);
        if (!same([...ids].sort(), [...selectedIds].sort())) return false;
      }
      return true;
    };

    const commit = (state, { updateMedia = true, updateUrl = false } = {}) => {
      const variant = state.payload.variant;
      state.groups.forEach((group, index) => {
        group.values.forEach((value, valueIndex) => { selectors[index].options[valueIndex].dataset.optionAvailable = value.available; });
        selectors[index].value = group.selectedName;
      });
      committedOptionIds = state.groups.map((group) => group.selectedId);
      if (!variant) {
        invalidate('unavailable', unavailableText);
      } else {
        idInput.value = variant.id;
        idInput.disabled = !variant.available;
        form.dataset.currentVariantId = variant.id;
        form.dataset.variantState = variant.available ? 'available' : 'sold-out';
        stateNode.textContent = JSON.stringify(state.payload);
        renderPrice(variant);
        renderPills();
        if (status) {
          status.textContent = variant.available ? status.dataset.inStock : status.dataset.outOfStock;
          status.classList.toggle('is-unavailable', !variant.available);
        }
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        syncPurchaseControls();
        if (updateMedia && variant.mediaId) {
          const media = Array.from(root.querySelectorAll('[data-media-id]'));
          const target = media.find((item) => item.dataset.mediaId === variant.mediaId);
          if (target && target.dataset.mediaActive !== 'true') {
            media.forEach((item) => { item.dataset.mediaActive = String(item === target); });
            root.querySelector('[data-product-media-gallery]')?.dispatchEvent(new CustomEvent('product:media-change', { detail: { mediaId: variant.mediaId } }));
            const counter = root.querySelector('[data-media-current]');
            if (counter && target.dataset.mediaPosition) counter.textContent = target.dataset.mediaPosition;
            target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
          }
        }
      }
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (variant) {
          url.searchParams.set('variant', variant.id);
          url.searchParams.delete('option_values');
        } else {
          url.searchParams.delete('variant');
          url.searchParams.set('option_values', selection().join(','));
        }
        if (url.href !== window.location.href) window.history.pushState(window.history.state, '', url);
      }
    };

    const requestState = async (url, { optionIds, updateUrl = false } = {}) => {
      const version = ++requestVersion;
      controller?.abort();
      controller = new AbortController();
      const activeController = controller;
      invalidate('loading', root.dataset.selectionLoading);
      const endpoint = new URL(url);
      endpoint.searchParams.set('section_id', root.dataset.sectionId);
      const timeout = window.setTimeout(() => activeController.abort(), 15000);
      try {
        const response = await fetch(endpoint, { signal: activeController.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Section request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (version !== requestVersion || !root.isConnected) return;
        const source = Array.from(html.querySelectorAll('[data-high-variant-product]')).find((node) =>
          node.dataset.highVariantProduct === root.dataset.highVariantProduct && node.dataset.sectionId === root.dataset.sectionId);
        const state = readState(source);
        if (!matchesUrl(state, url) || (optionIds && (!same(state.groups.map((group) => group.selectedId), optionIds) || !same(selection(), optionIds)))) {
          throw new Error('Selection mismatch');
        }
        commit(state, { updateUrl });
      } catch (error) {
        if (version === requestVersion && root.isConnected) invalidate('error', root.dataset.selectionError);
      } finally {
        window.clearTimeout(timeout);
      }
    };

    selectors.forEach((selector) => selector.addEventListener('change', () => {
      if (addLocked()) {
        selectors.forEach((item, index) => {
          item.value = Array.from(item.options).find((option) => option.dataset.optionValueId === committedOptionIds[index])?.value || '';
        });
        renderPills();
        syncPurchaseControls();
        return;
      }
      deferredHistoryUrl = null;
      const optionIds = selection();
      if (!optionIds.every(validId)) {
        ++requestVersion;
        controller?.abort();
        invalidate('unavailable', unavailableText);
        return;
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', optionIds.join(','));
      requestState(url, { optionIds, updateUrl: true });
    }));
    root.addEventListener('click', (event) => {
      if (addLocked() && event.target.closest?.('[data-option-pill], [data-option-selector]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    form.addEventListener('submit', (event) => {
      if (!canPurchase()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    payment?.addEventListener('click', (event) => {
      if (!canPurchase()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    const addStateObserver = new MutationObserver(syncPurchaseControls);
    addStateObserver.observe(form, { attributes: true, attributeFilter: ['data-add-state'] });
    const onPopState = () => {
      if (!root.isConnected) return;
      const url = new URL(window.location.href);
      if (addLocked()) deferredHistoryUrl = url;
      else requestState(url);
    };
    window.addEventListener('popstate', onPopState);
    root.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      ++requestVersion;
      controller?.abort();
      addStateObserver.disconnect();
      window.removeEventListener('popstate', onPopState);
    }, { once: true });
    try {
      const initialState = readState(root);
      if (!matchesUrl(initialState, new URL(window.location.href))) throw new Error('Invalid initial selection');
      commit(initialState, { updateMedia: false });
    } catch (error) {
      selectors.forEach((selector) => { selector.selectedIndex = -1; });
      invalidate('error', root.dataset.selectionError);
    }
  }

  bindLocalization() {
    document.querySelectorAll('.localization-form').forEach((form) => {
      if (form.dataset.localizationBound === 'true') return;
      form.dataset.localizationBound = 'true';
      form.querySelectorAll('select').forEach((select) => {
        select.addEventListener('change', () => form.submit());
      });
    });
  }

  bindDisclosure() {
    document.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
      if (trigger.dataset.accordionBound === 'true') return;
      trigger.dataset.accordionBound = 'true';
      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (panel) panel.hidden = expanded;
      });
    });
  }

  bindCategoryTabs() {
    const tabs = Array.from(document.querySelectorAll('[data-category-tab]'));
    const panels = Array.from(document.querySelectorAll('[data-category-panel]'));
    if (!tabs.length || !panels.length) return;

    const panelByKey = new Map(
      panels.map((panel) => [panel.dataset.categoryPanel, panel])
    );

    const activate = (tab, moveFocus = false) => {
      const target = tab.dataset.categoryTarget;
      const panel = panelByKey.get(target);
      if (!panel) return;

      tabs.forEach((candidate) => {
        const isActive = candidate === tab;
        candidate.classList.toggle('is-active', isActive);
        candidate.setAttribute('aria-selected', String(isActive));
        candidate.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((candidate) => {
        const isActive = candidate === panel;
        candidate.hidden = !isActive;
        candidate.classList.toggle('is-active', isActive);
        candidate.classList.toggle('is-hidden', !isActive);
      });

      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      if (tab.dataset.categoryBound === 'true') return;
      tab.dataset.categoryBound = 'true';
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        const navigationKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
        if (!navigationKeys.includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (index + 1) % tabs.length;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (index - 1 + tabs.length) % tabs.length;
        }
        activate(tabs[nextIndex], true);
      });
    });

    const selectedTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    activate(selectedTab);
  }

  bindRecommendations() {
    document.querySelectorAll('[data-product-recommendations]').forEach((element) => {
      const url = element.dataset.url;
      if (!url || element.dataset.loaded === 'true') return;
      element.dataset.loaded = 'true';

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const replacement = html.querySelector('[data-product-recommendations]');
          if (replacement && replacement.querySelector('.product-card')) {
            element.innerHTML = replacement.innerHTML;
            window.yevoriConversion?.bindQuickAdd(element);
          }
        })
        .catch(() => {});
    });
  }
}

const initAureliaTheme = () => {
  if (window.aureliaTheme) window.aureliaTheme.initialize();
  else window.aureliaTheme = new AureliaTheme();
};

document.addEventListener('DOMContentLoaded', initAureliaTheme);
document.addEventListener('shopify:section:load', initAureliaTheme);
